import type {
  CreateModulePayload,
  GetModulesPayload,
  Module,
  ModuleListItem,
  UpdateModulePayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type ModulesListResponse = { modules: ModuleListItem[] };
type AvailableModulesResponse = { modules: Module[] };
type ModuleResponse = { module: Module };

const moduleListTags = <T extends { id: string }>(result: T[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Module' as const, id })), { type: 'Module' as const, id: 'LIST' }]
    : [{ type: 'Module' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/module` (`moduleController`, prefix `/modules`) — the top of
 * the evaluation content hierarchy (module -> category -> question, question -> scale).
 *
 * `getModules` (admin list, `ModuleListItem`) and `getAvailableModules` (practitioner-facing,
 * `Module` with nested categories, used to pick a module when starting an evaluation session)
 * share the same `Module` `LIST` tag id since both are "list all modules" views.
 *
 * Cross-domain invalidation: `ModuleListItem.categories_count`/`questions_count` (and `Module`'s
 * optional `questions_count`) are counted live from the DB, not stored — so `category.api.ts`'s
 * create/delete and `question.api.ts`'s create/delete both invalidate `Module` `LIST` too (see
 * those files). The reverse also holds: `Category`'s and `Question`'s full shapes embed a live
 * `module: { id, title, code }` snippet (see `category.ts`/`question.ts`), so `updateModule` here
 * invalidates `Category` `LIST` and `Question` `LIST` as well — a title/code edit would otherwise
 * go stale in any already-cached category or question list.
 */
export const moduleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getModules: builder.query<ModuleListItem[], GetModulesPayload>({
      query: (payload) => ({ path: ['modules'], method: 'get', query: payload }),
      transformResponse: (response: ModulesListResponse) => response.modules,
      providesTags: (result) => moduleListTags(result),
    }),

    getAvailableModules: builder.query<Module[], void>({
      query: () => ({ path: ['modules', 'available'], method: 'get' }),
      transformResponse: (response: AvailableModulesResponse) => response.modules,
      providesTags: (result) => moduleListTags(result),
    }),

    getModuleById: builder.query<Module, string>({
      query: (id) => ({ path: ['modules', { param: 'module_id', value: id }], method: 'get' }),
      transformResponse: (response: ModuleResponse) => response.module,
      providesTags: (_result, _error, id) => [{ type: 'Module', id }],
    }),

    createModule: builder.mutation<Module, CreateModulePayload>({
      query: (body) => ({ path: ['modules'], method: 'post', body }),
      transformResponse: (response: ModuleResponse) => response.module,
      invalidatesTags: [{ type: 'Module', id: 'LIST' }],
    }),

    updateModule: builder.mutation<Module, { id: string; payload: UpdateModulePayload }>({
      query: ({ id, payload }) => ({
        path: ['modules', { param: 'module_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: ModuleResponse) => response.module,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Module', id },
        { type: 'Module', id: 'LIST' },
        { type: 'Category', id: 'LIST' },
        { type: 'Question', id: 'LIST' },
      ],
    }),

    deleteModule: builder.mutation<void, string>({
      query: (id) => ({ path: ['modules', { param: 'module_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Module', id },
        { type: 'Module', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetModulesQuery,
  useGetAvailableModulesQuery,
  useGetModuleByIdQuery,
  useCreateModuleMutation,
  useUpdateModuleMutation,
  useDeleteModuleMutation,
} = moduleApi;
